import { clone } from 'lodash-es'
import { useState } from 'react'
import { Middleware } from 'swr'

import { PaginationPayload } from '@/lib/core/components/Pagination'
import {
  NetworkResourcePaginatedResponse,
  PaginatedList,
  ResponseError,
  SortInfo,
} from '@/lib/core/interfaces/common.types'

import { RequestConfig, useNetworkRequest } from '@/lib/utils/request'

type ForwardParams<T, E> = Parameters<typeof useNetworkRequest<T, E>>

export type NetworkParams<T extends Partial<PaginationPayload> = Partial<PaginationPayload>> =
  | Partial<T>
  | null
  | undefined

export type SWROptions<Response, Error> = Omit<ForwardParams<Response, Error>[1], 'undefined'>

export const defaultFallback = {
  count: 0,
  next: null,
  previous: null,
  results: [],
}

export const createSchemaValidationMiddleware = <
  Response,
  Data extends PaginatedList<Response>,
  Raw = unknown,
>(
  schema: (data: Raw) => Response,
): Middleware => {
  return (useSWRNext) => {
    return (key, fetcher, config) => {
      const swr = useSWRNext(key, fetcher, config)

      if (swr.data) {
        const data = swr.data as unknown as Data
        data.results = data.results.map((item) => {
          try {
            return schema(item as unknown as Raw)
          } catch (error) {
            console.error('Data validation failed:', error)
            return item
          }
        })
      }

      return swr
    }
  }
}

export const usePaginatedNetworkResource = <
  Data,
  Params extends NetworkParams | null = NetworkParams | null,
  Response extends PaginatedList<Data> = PaginatedList<Data>,
  Error = ResponseError,
  SWRConfig extends SWROptions<Response, Error> = SWROptions<Response, Error>,
>(
  url: string | null,
  config: RequestConfig<Response, Params>,
  swrOptions?: SWRConfig,
): NetworkResourcePaginatedResponse<
  Data,
  Error,
  Response,
  { fallbackData: Response } & SWRConfig
> => {
  const [sortingInfo, setSortingInfo] = useState<SortInfo>({})
  const [page, setPage] = useState(config?.params?.page ?? 1)
  const [pageSize, setPageSize] = useState(config?.params?.pageSize ?? 20)

  const requestInfo = useNetworkRequest<Response, Error, { fallbackData: Response } & SWRConfig>(
    config?.params === null || url === null
      ? null
      : {
          url,
          config: {
            ...config,
            params: {
              page,
              pageSize,
              ordering: sortingInfo,
              ...config?.params,
            },
          },
        },
    {
      fallbackData: clone(defaultFallback) as unknown as Response,
      ...swrOptions,
    } as { fallbackData: Response } & SWRConfig,
  )

  return {
    ...requestInfo,
    page,
    pageSize,
    sortingInfo,
    setPage,
    setPageSize,
    setSortingInfo,
  }
}
